import type {
    DailyEventDto,
    DailyEventKind,
    DailyEventsDto,
    DailyHealthCauseDto,
    DailyOperationType,
    DailyPlatformStatusDto,
    HealthState,
    MetricDto,
} from "@/features/daily/types/daily.types";
import {
    CapabilityVm,
    DailyTone,
    ImpactRowVm,
    commandViewModel,
    dailyFormat,
} from "@/features/daily/components/command/command-view-model";
import { StatTileVm, statTileViewModel } from "@/features/daily/components/stat-tile-view-model";

/** `good` is earned, never defaulted: operational, measured, and with critical coverage. */
export type HealthTone = DailyTone | "good";

export type HealthHeroVm = {
    tone: HealthTone;
    title: string;
    description: string;
    reasons: string[];
    incidentsLabel: string;
};

/** D124/D125: the one-line state pill of the live bar; its tooltip holds the detail. */
export type StatusPillVm = {
    tone: HealthTone;
    label: string;
    tooltip: string[];
};

/** D125: how many live causes move the state, by severity. */
export type CauseCountsVm = {
    critical: number;
    warning: number;
};

/** D125: the compact alert strip that replaces the hero when the platform is not operational. */
export type AlertStripVm = {
    severityLabel: string;
    tone: HealthTone;
    title: string;
    causes: HealthCausesVm | null;
};

export type AttentionCountsVm = {
    critical: number;
    warning: number;
    openIncidents: number;
};

export type StatusStripVm = {
    tone: HealthTone;
    icon: "check_circle" | "error" | "help" | "info";
    title: string;
    detail: string | null;
    severityLabel: string | null;
    causes: HealthCausesVm | null;
};

export type ServiceVm = {
    key: string;
    name: string;
    statusLabel: string;
    tone: HealthTone;
    countsAsHealthy: boolean;
    evidenceModeLabel: string;
    evidenceLabel: string;
};

export type HistoricStuckVm = {
    countLabel: string;
    note: string;
    reasons: string[];
    funds: ImpactRowVm[];
};

export type HealthCauseVm = {
    key: string;
    text: string;
    href: string;
    normalization: string;
    tone: HealthTone;
};

export type HealthCausesVm = {
    /** `signal`: what moves the state, in the server's order. */
    affecting: HealthCauseVm[];
    /** `below_min_sample`: off target but too few cases to move the state. */
    warnings: HealthCauseVm[];
    /** `informative`: shown for context, never a verdict (e.g. deposits to regularize). */
    informative: HealthCauseVm[];
    /** `within_target`: the only group that may say «dentro de objetivo» — shown collapsed. */
    withinTarget: HealthCauseVm[];
};

type CauseStatus = NonNullable<DailyHealthCauseDto["status"]>;

export const QUIET_HOUR_COPY = "Horario tranquilo: es normal que no haya operaciones ahora";

export type IncidentRegisterVm =
    | { status: "unavailable"; title: string; body: string }
    | { status: "available"; openCount: number };

export type EventVm = {
    key: string;
    timeLabel: string;
    at: number;
    title: string;
    tone: DailyTone;
    detail: string;
};

const HERO_TITLE: Record<HealthState, string> = {
    operational: "La plataforma está operativa",
    degraded: "La plataforma funciona con degradación",
    outage: "La plataforma tiene una caída",
    unverifiable: "El estado de la plataforma no es verificable",
};

const EVENT_TEXT: Record<DailyEventKind, string> = {
    funds_received: "Fondos recibidos",
    exchange_completed: "Conversión completada",
    delivered: "Entrega confirmada",
    // B2 dates any `Failed` operation (deposits too), not only payouts.
    payout_failed: "Operación fallida",
};

const EVENT_TONE: Record<DailyEventKind, DailyTone> = {
    funds_received: "neutral",
    exchange_completed: "neutral",
    delivered: "neutral",
    payout_failed: "critical",
};

const OPERATION_TYPE_TEXT: Record<DailyOperationType, string> = {
    order: "Orden",
    deposit: "Depósito",
    trade: "Trade",
    withdrawal: "Retiro",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** D24: the resource cache stores `unknown`; the page proves the shape instead of casting. */
export const isEventsDto = (value: unknown): value is DailyEventsDto =>
    isRecord(value) && isRecord(value.envelope) && Array.isArray(value.events);

// The incident register is its own index. When it does not exist the status says
// `index_missing`, and "no incidents" would be a claim nobody can back.
const registerMissing = (dto: DailyPlatformStatusDto): boolean => dto.envelope.reasonCodes.includes("index_missing");

// D108 ruling: a critical capability that is merely quiet at this hour has no
// positive evidence. It never lets the hero turn green.
const criticalQuiet = (dto: DailyPlatformStatusDto): boolean =>
    dto.capabilities.some((capability) => capability.critical && capability.status === "expected_quiet");

const heroToneOf = (dto: DailyPlatformStatusDto): HealthTone => {
    const tone = commandViewModel.statusTone(dto.state, dto.envelope.quality);
    if (dto.state === "operational" && criticalQuiet(dto)) return "info";
    // B16: an unprovisioned incident register lowers the envelope to `partial`, not the
    // evidence behind the state — `evidenceFull` says so when the server sends it.
    const evidence = dto.evidenceFull === undefined ? dto.envelope.quality === "ok" : dto.evidenceFull;
    const earned = dto.state === "operational" && evidence && dto.coverageCritical;
    return earned ? "good" : tone;
};

const QUIET_TITLE = "Sin actividad esperada ahora";

const STATE_PILL: Record<Exclude<HealthState, "operational">, { label: string; tone: HealthTone }> = {
    degraded: { label: "DEGRADADA", tone: "warning" },
    outage: { label: "CAÍDA", tone: "critical" },
    unverifiable: { label: "NO VERIFICABLE", tone: "neutral" },
};

const ALERT_SEVERITY: Record<Exclude<HealthState, "operational">, { label: string; tone: HealthTone }> = {
    degraded: { label: "DEGRADADA", tone: "warning" },
    outage: { label: "CRÍTICO", tone: "critical" },
    unverifiable: { label: "NO VERIFICABLE", tone: "neutral" },
};

const SEVERITY_RANK: Record<NonNullable<DailyHealthCauseDto["severity"]>, number> = { info: 1, minor: 2, major: 3, critical: 4 };
const rankOf = (severity: DailyHealthCauseDto["severity"]): number => (severity === null ? 0 : SEVERITY_RANK[severity]);

const SEVERITY_TONE: Record<NonNullable<DailyHealthCauseDto["severity"]>, HealthTone> = {
    info: "info",
    minor: "info",
    major: "warning",
    critical: "critical",
};

const lowerFirst = (text: string): string => (text === "" ? text : `${text.charAt(0).toLowerCase()}${text.slice(1)}`);

/**
 * Guardian P1: a cause that does not move the state is NOT «within target» unless the
 * server says so. Before `status` existed a non-affecting cause stays informative.
 */
const statusOf = (cause: DailyHealthCauseDto): CauseStatus => {
    if (cause.affectsState) return "signal";
    return cause.status === undefined ? "informative" : cause.status;
};

const SMALL_SAMPLE = " · muestra pequeña";

/**
 * D117/B10: a cause in plain words — «225 depósitos con fondos recibidos sin acreditar»,
 * «1 de 267 entregas fuera de plazo — dentro de objetivo». Only the server's figures.
 */
const causeText = (cause: DailyHealthCauseDto): string => {
    const status = statusOf(cause);
    const where = cause.scope.kind === "platform" ? "" : ` · ${cause.scope.label}`;
    const small = status === "below_min_sample" ? SMALL_SAMPLE : "";
    if (cause.target === null) {
        const counted = cause.count === 0 && cause.sample === 0 ? cause.label : `${dailyFormat.count(cause.count)} ${lowerFirst(cause.label)}`;
        return `${counted}${small}${where}`;
    }
    const base = `${dailyFormat.count(cause.count)} de ${dailyFormat.count(cause.sample)} ${lowerFirst(cause.label)}`;
    const compliance = `${dailyFormat.ratioPercent(cause.ratio)} en plazo; objetivo ${dailyFormat.ratioPercent(cause.target)}`;
    const VERDICT: Record<CauseStatus, string> = {
        signal: ` — por debajo del objetivo (${compliance})`,
        within_target: " — dentro de objetivo",
        below_min_sample: ` (${compliance})${SMALL_SAMPLE}`,
        informative: ` (${compliance})`,
    };
    return `${base}${VERDICT[status]}${where}`;
};

const causeToneOf = (cause: DailyHealthCauseDto): HealthTone => {
    const status = statusOf(cause);
    if (status === "signal") return cause.severity === null ? "warning" : SEVERITY_TONE[cause.severity];
    if (status === "within_target") return "good";
    return status === "below_min_sample" ? "warning" : "neutral";
};

const causeVm = (cause: DailyHealthCauseDto, index: number): HealthCauseVm => ({
    key: `${cause.ruleId}:${cause.scope.key}:${index}`,
    text: causeText(cause),
    href: cause.queueHref,
    normalization: cause.normalization,
    tone: causeToneOf(cause),
});

const overdueSentence = (metric: MetricDto): string => {
    if (metric.value === null) return "Operaciones fuera de plazo: sin medir";
    if (metric.value === 0) return "Ninguna operación fuera de plazo";
    return `${dailyFormat.counted(metric.value, "operación", "operaciones")} fuera de plazo`;
};

const openIncidents = (dto: DailyPlatformStatusDto): number =>
    dto.incidents.filter((incident) => incident.closedAt === null).length;

const incidentsLabelOf = (dto: DailyPlatformStatusDto): string => {
    if (registerMissing(dto)) return "Registro de incidencias no disponible";
    const open = openIncidents(dto);
    if (open === 0) return "Sin incidencias activas";
    return open === 1 ? "1 incidencia activa" : `${dailyFormat.count(open)} incidencias activas`;
};

const valueToneOf = (metric: MetricDto, warnAbove: boolean): DailyTone => {
    if (metric.quality === "error") return "critical";
    if (warnAbove && metric.value !== null && metric.value > 0) return "warning";
    return "neutral";
};

const cardNote = (metric: MetricDto, measured: string): string =>
    metric.quality === "ok" ? measured : `${commandViewModel.qualityLabel(metric.quality)} · ${dailyFormat.counted(metric.sample, "operación", "operaciones")} en la población`;

const NO_CAPABILITIES = "Sin capacidades declaradas";

type CapabilitiesCountVm = { shown: number; verified: string[]; text: (noun: string) => string };

// B10: a capability the server marks as not displayed (`access`) is not counted on screen
// either, so the sentence matches the chips. With none declared there is nothing to verify.
const capabilitiesCount = (dto: DailyPlatformStatusDto): CapabilitiesCountVm => {
    const shown = commandViewModel.capabilities(dto.capabilities);
    const verified = commandViewModel.capabilities(dto.capabilities.filter((capability) => capability.status === "ok")).map((capability) => capability.label);
    return {
        shown: shown.length,
        verified,
        text: (noun) => (shown.length === 0 ? NO_CAPABILITIES : `${verified.length} de ${shown.length} ${noun}`),
    };
};

const openIncidentsLabel = (count: number): string =>
    dailyFormat.counted(count, "incidencia abierta", "incidencias abiertas");

export const healthViewModel = {
    hero: (dto: DailyPlatformStatusDto): HealthHeroVm => {
        const tone = heroToneOf(dto);
        const parts = [
            capabilitiesCount(dto).text("capacidades verificadas"),
            overdueSentence(dto.backlogOverdue),
        ];
        if (!dto.coverageCritical) parts.push("Cobertura crítica sin establecer: este estado no equivale a una salud completa");
        const quiet = dto.state === "operational" && criticalQuiet(dto);
        return {
            tone,
            title: quiet ? QUIET_TITLE : HERO_TITLE[dto.state],
            description: `${parts.join(". ")}.`,
            reasons: commandViewModel.reasonTexts(dto.envelope.reasonCodes),
            incidentsLabel: incidentsLabelOf(dto),
        };
    },

    /** D124: the tinted page background is earned exactly like the old green hero. */
    isHealthy: (dto: DailyPlatformStatusDto): boolean => heroToneOf(dto) === "good",

    statusPill: (dto: DailyPlatformStatusDto): StatusPillVm => {
        const hero = healthViewModel.hero(dto);
        const causes = healthViewModel.causes(dto);
        const group = (title: string, list: HealthCauseVm[]): string[] =>
            list.length === 0 ? [] : [`${title} (${list.length})`, ...list.map((cause) => cause.text)];
        const tooltip = [
            hero.title,
            hero.description,
            ...hero.reasons,
            hero.incidentsLabel,
            ...(causes === null ? [] : [
                ...group("Avisos", causes.warnings),
                ...group("Informativo", causes.informative),
                ...group("Dentro de objetivo", causes.withinTarget),
            ]),
        ];
        if (dto.state !== "operational") return { ...STATE_PILL[dto.state], tooltip };
        const capabilities = capabilitiesCount(dto);
        if (capabilities.shown === 0) return { label: NO_CAPABILITIES, tone: "neutral", tooltip };
        if (criticalQuiet(dto)) return { label: `Sin actividad esperada · ${capabilities.text("capacidades")}`, tone: "neutral", tooltip };
        // vb-metrics#38 U5: only the operative label names what was verified, and only from the data.
        const verifiedNote = capabilities.verified.length === 0
            ? "Ninguna capacidad comprobada con evidencia positiva en la última consulta."
            : `Verificadas en la última comprobación: ${capabilities.verified.join(", ")}.`;
        return { label: `Operativa · ${capabilities.text("capacidades")}`, tone: hero.tone === "good" ? "good" : "neutral", tooltip: [verifiedNote, ...tooltip] };
    },

    /** Live causes by severity; the open incidents stand in when the server sends no causes. */
    causeCounts: (dto: DailyPlatformStatusDto): CauseCountsVm => {
        const severities = dto.healthCauses === undefined
            ? dto.incidents.filter((incident) => incident.closedAt === null).map((incident) => incident.severity)
            : dto.healthCauses.filter((cause) => cause.affectsState).map((cause) => cause.severity);
        const critical = severities.filter((severity) => severity === "critical").length;
        const smallSample = (dto.healthCauses ?? []).filter((cause) => statusOf(cause) === "below_min_sample").length;
        return { critical, warning: severities.length - critical + smallSample };
    },

    /** `null` while operational. Otherwise the worst live cause (the server's order breaks ties). */
    alert: (dto: DailyPlatformStatusDto): AlertStripVm | null => {
        if (dto.state === "operational") return null;
        const causes = healthViewModel.causes(dto);
        const live = (dto.healthCauses ?? []).filter((cause) => cause.affectsState);
        const worst = live.reduce<DailyHealthCauseDto | null>(
            (best, cause) => (best === null || rankOf(cause.severity) > rankOf(best.severity) ? cause : best),
            null,
        );
        const reasons = commandViewModel.reasonTexts(dto.envelope.reasonCodes);
        const severity = ALERT_SEVERITY[dto.state];
        return {
            severityLabel: severity.label,
            tone: severity.tone,
            title: worst === null
                ? [HERO_TITLE[dto.state], ...reasons].join(" · ")
                : `${causeText(worst)}${live.length > 1 ? ` · +${live.length - 1} causas` : ""}`,
            causes,
        };
    },

    attentionCounts: (dto: DailyPlatformStatusDto): AttentionCountsVm => {
        const live = (dto.healthCauses ?? []).filter((cause) => cause.affectsState);
        const critical = live.filter((cause) => cause.severity === "critical").length;
        const smallSample = (dto.healthCauses ?? []).filter((cause) => statusOf(cause) === "below_min_sample").length;
        return { critical, warning: live.length - critical + smallSample, openIncidents: openIncidents(dto) };
    },

    // vb-metrics#38 U5: what needs a person wins over a state the server could not call
    // operational, which wins over a calm that was not earned; only then is it green.
    strip: (dto: DailyPlatformStatusDto): StatusStripVm => {
        const counts = healthViewModel.attentionCounts(dto);
        const checks = counts.critical + counts.warning;
        const alert = healthViewModel.alert(dto);
        const severityLabel = alert === null ? null : alert.severityLabel;
        const causes = alert === null ? null : alert.causes;
        if (checks > 0 || counts.openIncidents > 0) {
            const criticalIncident = dto.incidents.some((incident) => incident.closedAt === null && incident.severity === "critical");
            const parts = [
                ...(checks === 0 ? [] : [checks === 1 ? "1 comprobación en vivo necesita atención" : `${dailyFormat.count(checks)} comprobaciones en vivo necesitan atención`]),
                ...(counts.openIncidents === 0 ? [] : [openIncidentsLabel(counts.openIncidents)]),
            ];
            return {
                tone: alert === null ? (counts.critical > 0 || criticalIncident ? "critical" : "warning") : alert.tone,
                icon: "error",
                title: parts.join(" · "),
                detail: "Detalle en «Necesita atención».",
                severityLabel,
                causes,
            };
        }
        if (alert !== null) {
            return {
                tone: alert.tone,
                icon: alert.tone === "critical" || alert.tone === "warning" ? "error" : "help",
                title: alert.title,
                detail: null,
                severityLabel,
                causes,
            };
        }
        const tone = heroToneOf(dto);
        const register = healthViewModel.register(dto);
        // Without the register nobody can say nothing is open, whatever the evidence behind the state.
        if (register.status === "unavailable") {
            return { tone: tone === "good" ? "neutral" : tone, icon: "help", title: register.title, detail: register.body, severityLabel: null, causes: null };
        }
        if (tone !== "good") {
            const hero = healthViewModel.hero(dto);
            return { tone, icon: "info", title: hero.title, detail: [hero.description, ...hero.reasons].join(" "), severityLabel: null, causes: null };
        }
        return {
            tone,
            icon: "check_circle",
            title: "Nada fuera de objetivo en las comprobaciones en vivo",
            detail: healthViewModel.calmNote(dto),
            severityLabel: null,
            causes: null,
        };
    },

    needsAttention: (dto: DailyPlatformStatusDto): boolean => {
        const causes = healthViewModel.causes(dto);
        const listed = causes === null ? 0 : causes.affecting.length + causes.warnings.length + causes.informative.length;
        return openIncidents(dto) > 0 || listed > 0;
    },

    /** «hace 30 s» — the age of the last successful refresh, read at a glance. */
    refreshedAgo: (fetchedAt: number | null, now: number): string => {
        if (fetchedAt === null) return "sin refresco todavía";
        const seconds = Math.max(0, Math.floor((now - fetchedAt) / 1000));
        if (seconds < 60) return `hace ${seconds} s`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `hace ${minutes} min`;
        return `hace ${Math.floor(minutes / 60)} h`;
    },

    /** FR-007/SC-007: only a positively verified capability is ever green. */
    services: (capabilities: CapabilityVm[]): ServiceVm[] =>
        capabilities.map((capability) => ({
            key: capability.key,
            name: capability.label,
            statusLabel: capability.statusLabel,
            tone: capability.countsAsHealthy ? "good" : capability.tone,
            countsAsHealthy: capability.countsAsHealthy,
            evidenceModeLabel: capability.evidenceModeLabel,
            evidenceLabel: capability.lastEvidenceLabel === dailyFormat.absent
                ? "Sin evidencia registrada"
                : `Evidencia ${capability.lastEvidenceLabel}`,
        })),

    /**
     * The five stat tiles of «Operaciones de hoy». FR-008: the money waiting stays per
     * currency (the tile shows the first and says how many more; the tooltip lists each
     * exact amount) and an absent figure is a dash with its reason, never a zero.
     */
    todayTiles: (dto: DailyPlatformStatusDto, links: { overdue: string; open: string; historic: string; backlog: string }): StatTileVm[] => {
        const isToday = dto.envelope.window.label === "today";
        const windowLabel = commandViewModel.windowLabel(dto.envelope.window);
        const countTile = (key: string, label: string, metric: MetricDto, meta: string, note: string, warn: boolean, href: string | null): StatTileVm =>
            statTileViewModel.tile({
                key,
                label,
                value: dailyFormat.compactCount(metric.value),
                valueExact: metric.value === null ? `${label}: sin medir` : `${label}: ${dailyFormat.exactCount(metric.value)}`,
                valueTone: valueToneOf(metric, warn),
                meta: metric.quality === "ok" ? meta : commandViewModel.qualityLabel(metric.quality),
                tooltip: [cardNote(metric, note), ...commandViewModel.reasonTexts(metric.reasonCodes)],
                href,
                border: commandViewModel.metricTone(metric.quality),
            });
        const funds = commandViewModel.impactRows(dto.impactByCurrency);
        const firstFund = funds[0];
        const historic = healthViewModel.historicStuck(dto);
        const tiles: StatTileVm[] = [
            countTile(
                "completed",
                "Completadas",
                dto.throughputCompleted,
                isToday ? "desde las 00:00" : windowLabel,
                [
                    isToday ? "Finalizadas desde las 00:00 (Madrid)" : `Finalizadas · ${windowLabel}`,
                    // D145: what reconciles this tile with the funnel of today's cohort.
                    ...(dto.throughputCompletedCreatedBefore === undefined || dto.throughputCompletedCreatedBefore === null || dto.throughputCompletedCreatedBefore === 0
                        ? []
                        : [dto.throughputCompletedCreatedBefore === 1 ? "Incluye 1 creada antes de hoy" : `Incluye ${dailyFormat.exactCount(dto.throughputCompletedCreatedBefore)} creadas antes de hoy`]),
                ].join(" · "),
                false,
                null,
            ),
            countTile(
                "open",
                "En curso",
                dto.backlogOpen,
                dto.oldestOpenAgeMs === null ? "antigüedad sin medir" : `la más antigua ${dailyFormat.durationHours(dto.oldestOpenAgeMs)}`,
                dto.oldestOpenAgeMs === null
                    ? "Antigüedad de la más antigua: sin medir"
                    : `La más antigua lleva ${dailyFormat.durationHours(dto.oldestOpenAgeMs)} abierta`,
                false,
                links.open,
            ),
            countTile(
                "overdue",
                "Fuera de plazo",
                dto.backlogOverdue,
                dto.backlogOverdue.value === 0 ? "todas en plazo" : "abrir la cola",
                dto.backlogOverdue.value === 0 ? "Todas las abiertas dentro de plazo" : "Superaron el plazo de su etapa",
                true,
                links.overdue,
            ),
            statTileViewModel.tile({
                key: "funds",
                label: "Fondos",
                fullLabel: "Fondos en proceso",
                value: firstFund === undefined ? dailyFormat.absent : dailyFormat.compactMoney(firstFund.amount, firstFund.currency),
                valueExact: firstFund === undefined
                    ? "No hay dinero esperando en este alcance"
                    : funds.map((row) => dailyFormat.exactMoney(row.amount, row.currency)).join(" · "),
                meta: firstFund === undefined
                    ? "nada esperando"
                    : `${dailyFormat.counted(firstFund.sample, "operación", "operaciones")}${funds.length > 1 ? ` · +${funds.length - 1} monedas` : ""}`,
                tooltip: [
                    "Cada moneda por separado, nunca sumadas",
                    ...funds.map((row) => `${dailyFormat.exactMoney(row.amount, row.currency)} · ${dailyFormat.counted(row.sample, "operación", "operaciones")}`),
                ],
                href: links.open,
            }),
        ];
        // D122/B12: deposits with funds received and not credited for 7–30 days — shown
        // apart and calmly; only the younger ones move the live state.
        const backlog = dto.creditBacklog;
        if (backlog !== undefined) {
            const rows = commandViewModel.impactRows(dto.creditBacklogImpactByCurrency ?? []);
            const first = rows[0];
            tiles.push(statTileViewModel.tile({
                key: "credit-backlog",
                label: "Por regularizar",
                fullLabel: "Pendientes de regularizar",
                value: dailyFormat.compactCount(backlog.value),
                valueExact: backlog.value === null ? "Pendientes de regularizar: sin medir" : `Pendientes de regularizar: ${dailyFormat.exactCount(backlog.value)}`,
                meta: first === undefined
                    ? "7 a 30 días"
                    : `${dailyFormat.compactMoney(first.amount, first.currency)}${rows.length > 1 ? ` · +${rows.length - 1} monedas` : ""}`,
                tooltip: [
                    "Depósitos con fondos recibidos y sin acreditar desde hace entre 7 y 30 días. No degradan el estado en vivo.",
                    ...rows.map((row) => dailyFormat.exactMoney(row.amount, row.currency)),
                    ...commandViewModel.reasonTexts(backlog.reasonCodes),
                ],
                href: links.backlog,
            }));
        }
        if (historic !== null) {
            const first = historic.funds[0];
            tiles.push(statTileViewModel.tile({
                key: "historic",
                label: "Históricas",
                fullLabel: "Atascadas históricas",
                value: historic.countLabel,
                valueExact: `Atascadas históricas: ${historic.countLabel}`,
                meta: first === undefined
                    ? "más de 30 días"
                    : `${dailyFormat.compactMoney(first.amount, first.currency)}${historic.funds.length > 1 ? ` · +${historic.funds.length - 1} monedas` : ""}`,
                tooltip: [
                    historic.note,
                    ...historic.funds.map((row) => dailyFormat.exactMoney(row.amount, row.currency)),
                    ...historic.reasons,
                ],
                href: links.historic,
                border: "neutral",
            }));
        }
        return tiles;
    },

    /** D117/B10: every cause behind the state; `null` when the server sends none. */
    causes: (dto: DailyPlatformStatusDto): HealthCausesVm | null => {
        if (dto.healthCauses === undefined) return null;
        const groups: Record<CauseStatus, HealthCauseVm[]> = { signal: [], below_min_sample: [], informative: [], within_target: [] };
        dto.healthCauses.forEach((cause, index) => { groups[statusOf(cause)].push(causeVm(cause, index)); });
        return { affecting: groups.signal, warnings: groups.below_min_sample, informative: groups.informative, withinTarget: groups.within_target };
    },

    /**
     * D109/B7: operations stuck for more than 30 days no longer count as live overdue.
     * They are shown apart, calmly — `null` when the server does not send them.
     */
    historicStuck: (dto: DailyPlatformStatusDto): HistoricStuckVm | null => {
        const metric = dto.backlogHistoricStuck;
        if (metric === undefined) return null;
        return {
            countLabel: dailyFormat.exactCount(metric.value),
            note: metric.quality === "ok"
                ? "Más de 30 días sin moverse · no cuentan como fuera de plazo en vivo"
                : `${commandViewModel.qualityLabel(metric.quality)} · más de 30 días`,
            reasons: commandViewModel.reasonTexts(metric.reasonCodes),
            funds: commandViewModel.impactRows(dto.historicStuckImpactByCurrency ?? []),
        };
    },

    register: (dto: DailyPlatformStatusDto): IncidentRegisterVm =>
        registerMissing(dto)
            ? {
                status: "unavailable",
                title: "Registro de incidencias no disponible",
                body: "El registro de incidencias no está provisionado en esta fuente. Sin él no se puede afirmar que no haya incidencias: las cifras de operaciones de arriba siguen siendo válidas.",
            }
            : { status: "available", openCount: openIncidents(dto) },

    /** What the empty "needs attention" panel can honestly say about the open operations. */
    calmNote: (dto: DailyPlatformStatusDto): string => {
        const open = dto.backlogOpen.value;
        const overdue = dto.backlogOverdue.value;
        if (open === null || overdue === null) return "Las operaciones abiertas no se pudieron medir en esta consulta.";
        if (open === 0) return "No hay operaciones abiertas ahora mismo.";
        if (overdue === 0) {
            return open === 1
                ? "La operación abierta sigue dentro de plazo."
                : `Las ${dailyFormat.count(open)} operaciones abiertas siguen dentro de plazo.`;
        }
        return `${dailyFormat.count(overdue)} de ${dailyFormat.counted(open, "operación abierta", "operaciones abiertas")} superaron su plazo sin incidencia abierta.`;
    },

    events: (dtos: DailyEventDto[]): EventVm[] =>
        dtos
            .map((dto, index) => ({
                key: `${dto.operationId}:${dto.kind}:${dto.at}:${index}`,
                at: dto.at,
                timeLabel: dailyFormat.clock(dto.at),
                title: EVENT_TEXT[dto.kind],
                tone: EVENT_TONE[dto.kind],
                detail: `${OPERATION_TYPE_TEXT[dto.operationType]} · ${dto.operationId}`,
            }))
            .sort((left, right) => right.at - left.at),
};
